import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const AddCustomerModal = ({ onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    location: '',
    address: '',
    status: 'Active'
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const statusOptions = [
    { value: 'Active', label: 'Active' },
    { value: 'Inactive', label: 'Inactive' },
    { value: 'Pending', label: 'Pending' }
  ];

  const validateField = (field, value) => {
    let error = '';

    switch (field) {
      case 'name':
        if (!value?.trim()) {
          error = 'Full name is required';
        } else if (value?.trim()?.length < 2) {
          error = 'Name must be at least 2 characters';
        }
        break;
      case 'email':
        if (!value?.trim()) {
          error = 'Email address is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(value)) {
          error = 'Please enter a valid email address (e.g., user@example.com)';
        }
        break;
      case 'phone':
        if (!value?.trim()) {
          error = 'Phone number is required';
        } else if (!/^\+?[\d\s\-()]+$/?.test(value)) {
          error = 'Please enter a valid phone number';
        }
        break;
      case 'location':
        if (!value?.trim()) {
          error = 'Location is required';
        }
        break;
      case 'address':
        if (!value?.trim()) {
          error = 'Service address is required';
        } else if (value?.trim()?.length < 10) {
          error = 'Please enter a complete address';
        }
        break;
      default:
        break;
    }

    return error;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Real-time validation on change if field was touched
    if (touched?.[field]) {
      let error = validateField(field, value);
      setErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    let error = validateField(field, formData?.[field]);
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const validateForm = () => {
    const newErrors = {};
    const fieldsToValidate = ['name', 'email', 'phone', 'location', 'address'];

    fieldsToValidate?.forEach(field => {
      let error = validateField(field, formData?.[field]);
      if (error) {
        newErrors[field] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    // Mark all fields as touched
    const allTouched = {};
    Object.keys(formData)?.forEach(key => {
      allTouched[key] = true;
    });
    setTouched(allTouched);
    
    if (validateForm()) {
      // Pass the form data directly - parent will handle Supabase insert
      onSave(formData);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[1400] flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-elevation-5 w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <Icon name="UserPlus" size={20} color="var(--color-primary)" />
            </div>
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
              Add New Customer
            </h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={24} color="var(--color-foreground)" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-heading font-semibold text-foreground">
                Basic Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Full Name"
                  type="text"
                  placeholder="John Doe"
                  value={formData?.name}
                  onChange={(e) => handleInputChange('name', e?.target?.value)}
                  onBlur={() => handleBlur('name')}
                  error={touched?.name ? errors?.name : ''}
                  required
                />
                <Input
                  label="Email Address"
                  type="email"
                  placeholder="john.doe@example.com"
                  value={formData?.email}
                  onChange={(e) => handleInputChange('email', e?.target?.value)}
                  onBlur={() => handleBlur('email')}
                  error={touched?.email ? errors?.email : ''}
                  required
                />
                <Input
                  label="Phone Number"
                  type="tel"
                  placeholder="+1 (555) 123-4567"
                  value={formData?.phone}
                  onChange={(e) => handleInputChange('phone', e?.target?.value)}
                  onBlur={() => handleBlur('phone')}
                  error={touched?.phone ? errors?.phone : ''}
                  required
                />
                <Input
                  label="Company"
                  type="text"
                  placeholder="Acme Corporation"
                  value={formData?.company}
                  onChange={(e) => handleInputChange('company', e?.target?.value)}
                />
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-heading font-semibold text-foreground">
                Location Details
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="City, State"
                  type="text"
                  placeholder="New York, NY"
                  value={formData?.location}
                  onChange={(e) => handleInputChange('location', e?.target?.value)}
                  onBlur={() => handleBlur('location')}
                  error={touched?.location ? errors?.location : ''}
                  required
                />
                <Select
                  label="Account Status"
                  options={statusOptions}
                  value={formData?.status}
                  onChange={(value) => handleInputChange('status', value)}
                />
              </div>

              <Input
                label="Service Address"
                type="text"
                placeholder="123 Main Street, Suite 100"
                value={formData?.address}
                onChange={(e) => handleInputChange('address', e?.target?.value)}
                onBlur={() => handleBlur('address')}
                error={touched?.address ? errors?.address : ''}
                required
              />
            </div>

            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                <div className="text-sm text-muted-foreground">
                  <p className="font-medium text-foreground mb-1">Customer Profile Setup</p>
                  <p>After creating the customer profile, you can add equipment details and service history from the customer details page.</p>
                </div>
              </div>
            </div>
          </div>
        </form>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 p-4 md:p-6 border-t border-border">
          <Button
            variant="outline"
            fullWidth
            onClick={onClose}
            className="sm:flex-1"
          >
            Cancel
          </Button>
          <Button
            variant="default"
            fullWidth
            iconName="Save"
            iconPosition="left"
            onClick={handleSubmit}
            className="sm:flex-1"
          >
            Create Customer
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddCustomerModal;